import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { scheduleOrderProductionDay } from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import { orderTitle, tomorrowBusinessDate } from "@/lib/orderUtils";
import type { OrderDetail, OrderSummary } from "@/lib/types";

interface Props {
  /** Pedido sendo agendado; null fecha o dialog. */
  order: OrderSummary | null;
  onClose: () => void;
  onSaved: (order: OrderDetail) => void;
}

/**
 * Agenda o dia em que o pedido deve entrar em produção. Não avança o status — um scheduler avança
 * automaticamente os pedidos cujo dia agendado chegou (3x entre 3h e 4h, fuso de Brasília).
 */
export function ScheduleProductionDialog({ order, onClose, onSaved }: Props) {
  const [date, setDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (order) setDate(order.scheduledProductionDate ?? tomorrowBusinessDate());
  }, [order]);

  const alreadyScheduled = Boolean(order?.scheduledProductionDate);

  const save = async (nextDate: string | null) => {
    if (!order) return;
    setSubmitting(true);
    try {
      const updated = await scheduleOrderProductionDay(order.id, nextDate);
      onSaved(updated);
      toast.success(nextDate ? "Produção agendada" : "Agendamento removido");
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível agendar a produção"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={order !== null} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" /> Agendar dia de produção
          </DialogTitle>
          <DialogDescription>
            {order ? orderTitle(order) : ""}
            <span className="mt-1 block text-xs">
              O pedido entra em produção automaticamente na madrugada do dia escolhido. O status não muda agora.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="schedule-production-date">Dia da produção</Label>
          <DatePicker
            id="schedule-production-date"
            value={date}
            onChange={setDate}
            fromDate={tomorrowBusinessDate()}
          />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {alreadyScheduled ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => void save(null)}
              disabled={submitting}
            >
              Remover agendamento
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="hero"
              size="sm"
              onClick={() => void save(date)}
              disabled={submitting || !date}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {alreadyScheduled ? "Atualizar" : "Agendar"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
